import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/nb";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

export enum Language {
  Norwegian = "Norwegian",
  English = "English"
}

export type CurrencyFormatOptions = {
  language: Language;
  removeTrailingZeros?: boolean;
};

export const getLocale = (language: Language): "nb-NO" | "en-GB" =>
  language === Language.Norwegian ? "nb-NO" : "en-GB";

export const getDayjsLocale = (language: Language): "nb" | "en" => (language === Language.Norwegian ? "nb" : "en");

const getCurrencySuffix = (language: Language): string => (language === Language.Norwegian ? " kr" : " NOK");

const getDecimalSeparator = (language: Language): string => (language === Language.Norwegian ? "," : ".");

export const formatCurrencyValue = (amount: number | undefined | null, options: CurrencyFormatOptions): string => {
  if (amount === null || amount === undefined) {
    return "";
  }

  const { language, removeTrailingZeros = false } = options;
  const locale = getLocale(language);
  const suffix = getCurrencySuffix(language);

  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);

  if (!removeTrailingZeros) {
    return `${formatted}${suffix}`;
  }

  const decimalSeparator = getDecimalSeparator(language);
  const zeroFraction = `${decimalSeparator}00`;

  if (formatted.endsWith(zeroFraction)) {
    return `${formatted.slice(0, -zeroFraction.length)}${suffix}`;
  }

  return `${formatted}${suffix}`;
};

export const createCurrencyFormatter = (language: Language) =>
  new Intl.NumberFormat(getLocale(language), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

export const formatMonthName = (date: Date, language: Language): string =>
  date.toLocaleDateString(getLocale(language), { month: "long" });

export const parseToDate = (input: string | Date | null | undefined, language: Language): Date | null => {
  if (input === null || input === undefined || (typeof input === "string" && input.trim().length === 0)) {
    return null;
  }
  if (input instanceof Date) {
    const parsedDate = dayjs(input);
    return parsedDate.isValid() ? parsedDate.toDate() : null;
  }
  if (typeof input !== "string") {
    return null;
  }
  const trimmed = input.trim();
  const direct = dayjs(trimmed);
  if (direct.isValid()) {
    return direct.toDate();
  }
  const localeFormats: Partial<Record<string, string[]>> = {
    nb: [
      "D. MMMM YYYY [kl.] HH:mm",
      "D. MMM YYYY [kl.] HH:mm",
      "D. MMMM YYYY HH:mm",
      "D. MMM YYYY HH:mm",
      "DD.MM.YYYY HH:mm",
      "DD.MM.YYYY"
    ],
    en: [
      "D MMMM YYYY [at] HH:mm",
      "D MMM YYYY [at] HH:mm",
      "D MMMM YYYY HH:mm",
      "D MMM YYYY HH:mm",
      "MMMM D, YYYY HH:mm",
      "MMMM D, YYYY",
      "DD/MM/YYYY HH:mm",
      "DD/MM/YYYY"
    ]
  };
  const dayjsLocale = getDayjsLocale(language);
  const localeCandidates = dayjsLocale === "nb" ? ["nb", "en"] : ["en", "nb"];
  for (const localeCandidate of localeCandidates) {
    const formats = localeFormats[localeCandidate];
    if (!formats) {
      continue;
    }
    const localized = dayjs(trimmed, formats, localeCandidate, true);
    if (localized.isValid()) {
      return localized.toDate();
    }
  }
  const strictFormats = [
    "YYYY-MM-DD HH:mm",
    "YYYY-MM-DDTHH:mm",
    "YYYY-MM-DDTHH:mmZ",
    "YYYY-MM-DDTHH:mm:ss",
    "YYYY-MM-DDTHH:mm:ssZ",
    "YYYY-MM-DDTHH:mm:ss.SSSZ"
  ];
  const strict = dayjs(trimmed, strictFormats, true);
  if (strict.isValid()) {
    return strict.toDate();
  }
  return null;
};

export const createDateFormatter = (language: Language, options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat(getLocale(language), options);

export const formatDateTime = (input: string | Date | null | undefined, language: Language): string => {
  if (input === null || input === undefined || (typeof input === "string" && input.trim().length === 0)) {
    return "";
  }
  const date = parseToDate(input, language);
  if (!date) {
    return String(input);
  }
  const locale = getLocale(language);
  const datePart = createDateFormatter(language, {
    day: "numeric",
    month: "long",
    year: "numeric"
  }).format(date);
  const timePart = createDateFormatter(language, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).format(date);
  return locale === "nb-NO" ? `${datePart} kl. ${timePart}` : `${datePart}, ${timePart}`;
};

export const formatDate = (input: string | Date | null | number | undefined, language: Language): string => {
  if (
    input === null ||
    input === undefined ||
    typeof input === "number" ||
    (typeof input === "string" && input.trim().length === 0)
  ) {
    return "";
  }
  const date = parseToDate(input, language);
  if (!date) {
    return String(input);
  }
  return createDateFormatter(language, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  }).format(date);
};

export const formatTime = (input: string | Date | null | undefined, language: Language): string => {
  if (input === null || input === undefined || (typeof input === "string" && input.trim().length === 0)) {
    return "";
  }
  const date = parseToDate(input, language);
  if (!date) {
    return String(input);
  }
  return createDateFormatter(language, {
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
};

export const formatDateTimeInput = (input: string | Date | null | undefined, language: Language): string => {
  const date = parseToDate(input, language);
  if (!date) {
    return "";
  }
  return dayjs(date).format("YYYY-MM-DDTHH:mm");
};

export const formatCardExpiry = (input: string | Date | null | undefined, language: Language): string => {
  if (input === null || input === undefined || (typeof input === "string" && input.trim().length === 0)) {
    return "";
  }
  const date = parseToDate(input, language);
  if (date) {
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const year = `${date.getFullYear() % 100}`.padStart(2, "0");
    return `${month}/${year}`;
  }
  const normalized = String(input).trim();
  const re = /^(\d{2})[./-]?(\d{2})$/;
  const match = re.exec(normalized);
  if (match) {
    return `${match[1]}/${match[2]}`;
  }
  return normalized;
};

export const formatLicensePlate = (plate: string | null | undefined): string => {
  if (typeof plate !== "string") {
    return "";
  }
  return plate.trim().toUpperCase();
};

export type DurationParts = {
  days: number;
  hours: number;
  minutes: number;
  seconds?: number;
};

export type TranslateFn = (key: string, values?: Record<string, string | number | null | undefined>) => string;

export const formatDuration = (parts: DurationParts | null | undefined, translate: TranslateFn): string => {
  if (!parts) {
    return "";
  }
  const segments: string[] = [];
  const append = (count: number, singular: string, plural: string) => {
    if (count <= 0) {
      return;
    }
    const key = count === 1 ? singular : plural;
    segments.push(translate(key, { count }));
  };
  append(parts.days, "common.duration.units.day", "common.duration.units.dayPlural");
  append(parts.hours, "common.duration.units.hour", "common.duration.units.hourPlural");
  append(parts.minutes, "common.duration.units.minute", "common.duration.units.minutePlural");
  if (segments.length === 0) {
    append(parts.seconds ?? 0, "common.duration.units.second", "common.duration.units.secondPlural");
  }
  if (segments.length <= 1) {
    return segments.join(", ");
  }
  const lastSegment = segments.pop() ?? "";
  const conjunctionTranslation = translate("common.duration.conjunction");
  const conjunction =
    conjunctionTranslation && conjunctionTranslation !== "common.duration.conjunction" ? conjunctionTranslation : "and";
  return `${segments.join(", ")} ${conjunction} ${lastSegment}`;
};

export const formatDurationLabel = (parts: DurationParts | null | undefined, translate: TranslateFn): string => {
  const value = formatDuration(parts, translate);
  if (!value) {
    return "";
  }
  return translate("common.duration.label", { duration: value });
};
